// MARK://dhk8/admin-shell/lib/clients/recipes-catalog
// Recetario standalone → dhk8-catalog-recipes (en prod).
//   GET    /v1/recipes?tenantId=...&status=...        → { items: RecipeDto[], total, page, limit }
//   GET    /v1/recipes/:id?tenantId=...               → RecipeDto
//   POST   /v1/recipes        body { tenantId, name, productId?, yieldServings?, kdsStation?, prepTimeMin? }
//   PATCH  /v1/recipes/:id    body { name?, productId?, active? }
//   DELETE /v1/recipes/:id
//   POST   /v1/recipes/:id/{publish,unpublish,archive,duplicate}
//   PATCH  /v1/recipes/:id/{station,prep-time,yield,allergens}
//   POST   /v1/recipes/:id/ingredients     body { productId, quantity, unit }
//   PATCH  /v1/recipes/:id/ingredients/:ingredientId   body { quantity }
//   DELETE /v1/recipes/:id/ingredients/:ingredientId
//
// CONTRACT NOTE: el backend devuelve `{ ok, data }` → usar apiData. `status` ya
// es un enum estable ('draft'|'published'|'archived'); `cost` lo recalcula el
// backend al cambiar ingredientes. Las cantidades de ingrediente viajan como
// string decimal (`^\d+(\.\d{1,4})?$`). Es distinto de `recipes.ts` (plomería
// del onboarding) — esta es la vista de recetario propiamente dicha.
import { svcUrl, apiData } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'
import { authTenantId } from '../../stores/auth-store.js'
import { gqlClient } from '../gql.js'
import { AdminRecipesQuery, AdminRecipeQuery, AdminCreateRecipeMutation, AdminUpdateRecipeMutation, AdminDeleteRecipeMutation, AdminPublishRecipeMutation, AdminUnpublishRecipeMutation, AdminRecalcRecipeCostMutation, AdminAddRecipeIngredientMutation, AdminUpdateRecipeIngredientMutation, AdminRemoveRecipeIngredientMutation, AdminLinkSubRecipeMutation, AdminUnlinkSubRecipeMutation } from '../queries/recipes.queries.js'

const RECIPES_SVC = 'dhk8-catalog-recipes'

export type RecipeStatus = 'draft' | 'published' | 'archived'

export interface RecipeIngredient {
  id: string
  productId: string
  stockItemId: string | null
  quantity: string
  unit: string
  wasteFactor: string
  optional: boolean
  sortOrder: number
}

export interface Recipe {
  id: string
  tenantId: string
  productId: string | null
  variantSkuId: string | null
  name: string
  yieldServings: number | null
  yieldPercent: string
  yieldQuantity: number
  yieldUnit: string
  cost: number | null
  costMissing: number
  active: boolean
  status: RecipeStatus
  allergens: string[]
  kdsStation: string | null
  prepTimeMin: number | null
  nutrition: Record<string, unknown> | null
  imageUrl: string | null
  ingredients: RecipeIngredient[]
  subRecipes: Array<{ subId: string; name: string; quantity: string }>
  createdAt: string
  updatedAt: string
}

export interface CreateRecipeInput {
  name: string
  productId?: string
  variantSkuId?: string
  yieldServings?: number
  // B / CANON §1.7 — rendimiento canónico (cantidad + clave SAT). Viajan como
  // string al grafo (RecipeCreateInput.yieldQuantity es String).
  yieldQuantity?: number
  yieldUnit?: string
  kdsStation?: string
  prepTimeMin?: number
}

const STATUS_LABEL: Record<RecipeStatus, string> = {
  draft: 'Borrador',
  published: 'Publicada',
  archived: 'Archivada',
}
export function recipeStatusLabel(s: RecipeStatus): string {
  return STATUS_LABEL[s] ?? s
}

const STATIONS: Array<{ id: string; label: string }> = [
  { id: 'barra', label: 'Barra / Bebidas' },
  { id: 'cocina-fria', label: 'Cocina fría' },
  { id: 'cocina-caliente', label: 'Cocina caliente' },
  { id: 'plancha', label: 'Plancha' },
  { id: 'postres', label: 'Postres' },
]
export const KDS_STATIONS = STATIONS

const ALLERGENS = ['Gluten', 'Lácteos', 'Huevo', 'Soya', 'Cacahuate', 'Frutos secos', 'Mariscos', 'Pescado', 'Sésamo']
export const ALLERGENS_CATALOG = ALLERGENS

// ── preview store ──────────────────────────────────────────────────────────
const now = () => new Date().toISOString()
const db: { recipes: Recipe[] } = {
  recipes: [
    {
      id: 'rec-cap', tenantId: 'tnt_demo', productId: null, variantSkuId: null, name: 'Cappuccino',
      yieldServings: 1, yieldPercent: '100', yieldQuantity: 1, yieldUnit: 'H87', cost: 1280, costMissing: 0, active: true, status: 'published',
      allergens: ['Lácteos'], kdsStation: 'barra', prepTimeMin: 4, nutrition: null, imageUrl: null,
      ingredients: [
        { id: 'ing-1', productId: 'p-cafe', stockItemId: null, quantity: '18', unit: 'g', wasteFactor: '0', optional: false, sortOrder: 0 },
        { id: 'ing-2', productId: 'p-leche', stockItemId: null, quantity: '150', unit: 'ml', wasteFactor: '0', optional: false, sortOrder: 1 },
      ],
      subRecipes: [], createdAt: now(), updatedAt: now(),
    },
  ],
}
const mkid = (p: string) => `${p}-${Math.random().toString(36).slice(2, 8)}`

// Mapea la Recipe del grafo (typed-document-node) al tipo de UI. nutrition se
// deja null (no se expone en el grafo). Los nombres de insumo los resuelve la UI
// vía el listado de productos (también grafo, F1.1) o la federación.
interface GqlRecipe {
  id: string; tenantId: string; productId?: string | null; variantSkuId?: string | null; name: string
  yieldServings?: number | null; yieldPercent: string; yieldQuantity?: number | null; yieldUnit?: string | null; cost?: number | null; costMissing?: number | null
  active: boolean; status: string; allergens: ReadonlyArray<string>
  kdsStation?: string | null; prepTimeMin?: number | null; imageUrl?: string | null
  createdAt: string; updatedAt: string
  ingredients: ReadonlyArray<{ id: string; productId?: string | null; stockItemId?: string | null; quantity: string; unit: string; wasteFactor?: string | null; optional?: boolean | null; sortOrder?: number | null }>
  subRecipes: ReadonlyArray<{ subId: string; name: string; quantity: string }>
}
function toUiRecipe(r: GqlRecipe): Recipe {
  return {
    id: r.id, tenantId: r.tenantId, productId: r.productId ?? null, variantSkuId: r.variantSkuId ?? null, name: r.name,
    yieldServings: r.yieldServings ?? null, yieldPercent: r.yieldPercent,
    yieldQuantity: r.yieldQuantity ?? 1, yieldUnit: r.yieldUnit ?? 'H87', cost: r.cost ?? null, costMissing: r.costMissing ?? 0,
    active: r.active, status: r.status as RecipeStatus, allergens: [...r.allergens],
    kdsStation: r.kdsStation ?? null, prepTimeMin: r.prepTimeMin ?? null,
    nutrition: null, imageUrl: r.imageUrl ?? null,
    ingredients: r.ingredients.map((i) => ({ id: i.id, productId: i.productId ?? '', stockItemId: i.stockItemId ?? null, quantity: i.quantity, unit: i.unit, wasteFactor: i.wasteFactor ?? '0', optional: i.optional ?? false, sortOrder: i.sortOrder ?? 0 })),
    subRecipes: r.subRecipes.map((s) => ({ subId: s.subId, name: s.name, quantity: s.quantity })),
    createdAt: r.createdAt, updatedAt: r.updatedAt,
  }
}

export async function listRecipes(signal?: AbortSignal, status?: RecipeStatus): Promise<Recipe[]> {
  if (PREVIEW_STUB) return mock(status ? db.recipes.filter((r) => r.status === status) : db.recipes)
  // F1.6 / ADR-0009: lectura por el grafo (tenant del JWT, RLS). Escrituras siguen REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminRecipesQuery, { limit: 100, ...(status ? { status } : {}) }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.recipes.items ?? []).map(toUiRecipe)
}

export async function getRecipe(id: string, signal?: AbortSignal): Promise<Recipe> {
  if (PREVIEW_STUB) {
    const r = db.recipes.find((x) => x.id === id)
    if (!r) throw new Error('Receta no encontrada')
    return mock(r)
  }
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminRecipeQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const r = res.data?.recipe
  if (!r) throw new Error('Receta no encontrada')
  return toUiRecipe(r)
}

export async function createRecipe(input: CreateRecipeInput): Promise<Recipe> {
  if (PREVIEW_STUB) {
    const r: Recipe = {
      id: mkid('rec'), tenantId: 'tnt_demo', productId: input.productId ?? null, variantSkuId: input.variantSkuId ?? null, name: input.name,
      yieldServings: input.yieldServings ?? 1, yieldPercent: '100', yieldQuantity: input.yieldQuantity ?? 1, yieldUnit: input.yieldUnit ?? 'H87', cost: null, costMissing: 0, active: true, status: 'draft',
      allergens: [], kdsStation: input.kdsStation ?? null, prepTimeMin: input.prepTimeMin ?? null,
      nutrition: null, imageUrl: null, ingredients: [], subRecipes: [], createdAt: now(), updatedAt: now(),
    }
    db.recipes.unshift(r)
    return mock(r)
  }
  // F9.4 / ADR-0009: alta por el grafo (tenant del JWT, RLS). Sin tenantId en el input.
  // yieldQuantity viaja como string (RecipeCreateInput.yieldQuantity es String).
  const res = await gqlClient.mutation(AdminCreateRecipeMutation, {
    input: {
      name: input.name,
      ...(input.productId ? { productId: input.productId } : {}),
      ...(input.variantSkuId ? { variantSkuId: input.variantSkuId } : {}),
      ...(input.yieldServings != null ? { yieldServings: input.yieldServings } : {}),
      ...(input.yieldQuantity != null ? { yieldQuantity: String(input.yieldQuantity) } : {}),
      ...(input.yieldUnit ? { yieldUnit: input.yieldUnit } : {}),
      ...(input.kdsStation ? { kdsStation: input.kdsStation } : {}),
      ...(input.prepTimeMin != null ? { prepTimeMin: input.prepTimeMin } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.createRecipe
  if (!r) throw new Error('createRecipe: empty response')
  return toUiRecipe(r)
}

export async function updateRecipe(id: string, patch: { name?: string; productId?: string | null; active?: boolean; yieldQuantity?: number; yieldUnit?: string }): Promise<Recipe> {
  if (PREVIEW_STUB) {
    const r = db.recipes.find((x) => x.id === id)
    if (!r) throw new Error('Receta no encontrada')
    Object.assign(r, patch, { updatedAt: now() })
    return mock(r)
  }
  // yieldQuantity viaja como string (RecipeUpdateInput.yieldQuantity es String).
  const res = await gqlClient.mutation(AdminUpdateRecipeMutation, {
    id,
    input: {
      ...(patch.name !== undefined ? { name: patch.name } : {}),
      ...(patch.productId !== undefined ? { productId: patch.productId } : {}),
      ...(patch.active !== undefined ? { active: patch.active } : {}),
      ...(patch.yieldQuantity !== undefined ? { yieldQuantity: String(patch.yieldQuantity) } : {}),
      ...(patch.yieldUnit !== undefined ? { yieldUnit: patch.yieldUnit } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.updateRecipe
  if (!r) throw new Error('updateRecipe: empty response')
  return toUiRecipe(r)
}

export async function deleteRecipe(id: string): Promise<void> {
  if (PREVIEW_STUB) { db.recipes = db.recipes.filter((x) => x.id !== id); return mock(undefined) }
  const res = await gqlClient.mutation(AdminDeleteRecipeMutation, { id }).toPromise()
  if (res.error) throw res.error
}

async function transition(id: string, action: 'publish' | 'unpublish' | 'archive' | 'duplicate'): Promise<Recipe> {
  if (PREVIEW_STUB) {
    if (action === 'duplicate') {
      const src = db.recipes.find((x) => x.id === id)
      if (!src) throw new Error('Receta no encontrada')
      const copy: Recipe = { ...structuredClone(src), id: mkid('rec'), name: `${src.name} (copia)`, status: 'draft' }
      db.recipes.unshift(copy)
      return mock(copy)
    }
    const r = db.recipes.find((x) => x.id === id)
    if (!r) throw new Error('Receta no encontrada')
    r.status = action === 'publish' ? 'published' : action === 'unpublish' ? 'draft' : 'archived'
    r.updatedAt = now()
    return mock(r)
  }
  // F9.4: publish/unpublish por el grafo (mutations); archive/duplicate siguen REST
  // (no se expusieron como mutations en F9.4 — fuera de alcance del CRUD nuclear).
  if (action === 'publish') {
    const res = await gqlClient.mutation(AdminPublishRecipeMutation, { id }).toPromise()
    if (res.error) throw res.error
    const r = res.data?.publishRecipe
    if (!r) throw new Error('publishRecipe: empty response')
    return toUiRecipe(r)
  }
  if (action === 'unpublish') {
    const res = await gqlClient.mutation(AdminUnpublishRecipeMutation, { id }).toPromise()
    if (res.error) throw res.error
    const r = res.data?.unpublishRecipe
    if (!r) throw new Error('unpublishRecipe: empty response')
    return toUiRecipe(r)
  }
  return apiData<Recipe>(svcUrl(RECIPES_SVC, `/v1/recipes/${id}/${action}`), { method: 'POST' })
}
export const publishRecipe = (id: string) => transition(id, 'publish')
export const unpublishRecipe = (id: string) => transition(id, 'unpublish')
export const archiveRecipe = (id: string) => transition(id, 'archive')
export const duplicateRecipe = (id: string) => transition(id, 'duplicate')

/** I1: recalcula el costo real (insumo×conversión×merma, vía erp-inventory) y devuelve la receta. */
export async function recalcRecipeCost(id: string): Promise<Recipe> {
  const res = await gqlClient.mutation(AdminRecalcRecipeCostMutation, { id }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.recalcRecipeCost
  if (!r) throw new Error('recalcRecipeCost: empty response')
  return toUiRecipe(r)
}

// F6.2 R3a / ADR-0008: los 4 setters operativos van por el grafo (updateRecipe,
// tenant del JWT, IDOR-guard). El resolver enruta cada campo a su método de
// dominio dedicado (station_assigned/preparation_time_set/yield_set/allergen_tagged).
// Sustituye los PATCH REST /v1/recipes/:id/{station,prep-time,yield,allergens}.
async function updateRecipeFields(id: string, input: { kdsStation?: string; prepTimeMin?: number; yieldServings?: number; allergens?: string[] }): Promise<Recipe> {
  const res = await gqlClient.mutation(AdminUpdateRecipeMutation, { id, input }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.updateRecipe
  if (!r) throw new Error('updateRecipe: empty response')
  return toUiRecipe(r)
}
export async function setStation(id: string, kdsStation: string): Promise<Recipe> {
  if (PREVIEW_STUB) { const r = db.recipes.find((x) => x.id === id)!; r.kdsStation = kdsStation; return mock(r) }
  return updateRecipeFields(id, { kdsStation })
}
export async function setPrepTime(id: string, prepTimeMin: number): Promise<Recipe> {
  if (PREVIEW_STUB) { const r = db.recipes.find((x) => x.id === id)!; r.prepTimeMin = prepTimeMin; return mock(r) }
  return updateRecipeFields(id, { prepTimeMin })
}
export async function setYield(id: string, yieldServings: number): Promise<Recipe> {
  if (PREVIEW_STUB) { const r = db.recipes.find((x) => x.id === id)!; r.yieldServings = yieldServings; return mock(r) }
  return updateRecipeFields(id, { yieldServings })
}
export async function setAllergens(id: string, allergens: string[]): Promise<Recipe> {
  if (PREVIEW_STUB) { const r = db.recipes.find((x) => x.id === id)!; r.allergens = allergens; return mock(r) }
  return updateRecipeFields(id, { allergens })
}

/** quantity must be a decimal string the server accepts (`^\d+(\.\d{1,4})?$`). */
function qtyStr(n: number): string {
  return (n.toFixed(4).replace(/\.?0+$/, '') || '0')
}
export async function addIngredient(recipeId: string, input: { productId: string; quantity: number; unit: string; wasteFactor?: number; optional?: boolean }): Promise<void> {
  if (PREVIEW_STUB) {
    const r = db.recipes.find((x) => x.id === recipeId)!
    r.ingredients.push({ id: mkid('ing'), productId: input.productId, stockItemId: null, quantity: qtyStr(input.quantity), unit: input.unit, wasteFactor: qtyStr(input.wasteFactor ?? 0), optional: input.optional ?? false, sortOrder: r.ingredients.length })
    return mock(undefined)
  }
  // wasteFactor viaja como string decimal (RecipeIngredientAddInput.wasteFactor es String).
  const res = await gqlClient.mutation(AdminAddRecipeIngredientMutation, {
    recipeId,
    input: {
      productId: input.productId, quantity: qtyStr(input.quantity), unit: input.unit,
      ...(input.wasteFactor != null ? { wasteFactor: qtyStr(input.wasteFactor) } : {}),
      ...(input.optional != null ? { optional: input.optional } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
}
export async function updateIngredientQty(recipeId: string, ingredientId: string, input: { quantity: number; unit?: string; wasteFactor?: number; optional?: boolean }): Promise<void> {
  if (PREVIEW_STUB) {
    const r = db.recipes.find((x) => x.id === recipeId)!
    const ing = r.ingredients.find((i) => i.id === ingredientId)
    if (ing) {
      ing.quantity = qtyStr(input.quantity)
      if (input.unit !== undefined) ing.unit = input.unit
      if (input.wasteFactor !== undefined) ing.wasteFactor = qtyStr(input.wasteFactor)
      if (input.optional !== undefined) ing.optional = input.optional
    }
    return mock(undefined)
  }
  const res = await gqlClient.mutation(AdminUpdateRecipeIngredientMutation, {
    recipeId,
    ingredientId,
    input: {
      quantity: qtyStr(input.quantity),
      ...(input.unit !== undefined ? { unit: input.unit } : {}),
      ...(input.wasteFactor !== undefined ? { wasteFactor: qtyStr(input.wasteFactor) } : {}),
      ...(input.optional !== undefined ? { optional: input.optional } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
}
export async function removeIngredient(recipeId: string, ingredientId: string): Promise<void> {
  if (PREVIEW_STUB) {
    const r = db.recipes.find((x) => x.id === recipeId)!
    r.ingredients = r.ingredients.filter((i) => i.id !== ingredientId)
    return mock(undefined)
  }
  const res = await gqlClient.mutation(AdminRemoveRecipeIngredientMutation, { recipeId, ingredientId }).toPromise()
  if (res.error) throw res.error
}

// ── F9.4: sub-recetas (vincular una receta hija como componente; quantity = string decimal) ──
export async function linkSubRecipe(parentId: string, input: { subId: string; quantity: number }): Promise<void> {
  if (PREVIEW_STUB) {
    const r = db.recipes.find((x) => x.id === parentId)!
    const child = db.recipes.find((x) => x.id === input.subId)
    r.subRecipes = [...r.subRecipes.filter((s) => s.subId !== input.subId), { subId: input.subId, name: child?.name ?? '(receta)', quantity: qtyStr(input.quantity) }]
    return mock(undefined)
  }
  const res = await gqlClient.mutation(AdminLinkSubRecipeMutation, {
    parentId,
    input: { subId: input.subId, quantity: qtyStr(input.quantity) },
  }).toPromise()
  if (res.error) throw res.error
}
export async function unlinkSubRecipe(parentId: string, subId: string): Promise<void> {
  if (PREVIEW_STUB) {
    const r = db.recipes.find((x) => x.id === parentId)!
    r.subRecipes = r.subRecipes.filter((s) => s.subId !== subId)
    return mock(undefined)
  }
  const res = await gqlClient.mutation(AdminUnlinkSubRecipeMutation, { parentId, subId }).toPromise()
  if (res.error) throw res.error
}
